import { ButtonBase, styled } from '@mui/material';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import { PageContext } from 'pages/Teams/PageProvider';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    ResponsiveDialog,
    Stack,
    Text,
    TextInput,
} from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

// given an array of items and a number size, return an array with subarrays with at most size items
const chunk = (items, size) => {
    const chunks = [];
    items = [].concat(...items);
    while (items.length) {
        chunks.push(items.splice(0, size));
    }
    return chunks;
};

const collapseTeamActivities = (id, activities) => {
    const creates = activities.filter(({ action }) => action === 'create');
    const updates = activities.filter(({ action }) => action === 'update');
    const deletes = activities.filter(({ action }) => action === 'delete');
    if (creates.length && deletes.length) return null;
    if (!creates.length && deletes.length) {
        return {
            type: 'group',
            action: 'delete',
            payload: { id },
        };
    }
    if (creates.length) {
        return {
            type: 'group',
            action: 'create',
            payload: {
                id,
                name: updates.length ? updates[updates.length - 1].payload.name : creates[0].payload.name,
                number: creates[0].payload.number,
            },
        };
    }
    return {
        type: 'group',
        action: 'update',
        payload: {
            id,
            name: updates[updates.length - 1].payload.name,
        },
    };
};

const collapseStudentActivities = (id, activities) => {
    return {
        type: 'member',
        action: 'move',
        payload: {
            id,
            from: activities[0].payload.from,
            to: activities[activities.length - 1].payload.to,
        },
    };
};

const getChanges = (activities) => {
    const sorted = activities.sort((a, b) => a.created_on - b.created_on);
    const teamActivities = sorted.filter(({ action }) => ['create', 'update', 'delete'].includes(action));
    const studentActivities = sorted.filter(({ action }) => ['move'].includes(action));
    const reducedTeamActivities = teamActivities.reduce((acc, cur) => {
        return {
            ...acc,
            [cur.id]: [...(acc[cur.id] || []), cur],
        };
    }, {});
    const reducedStudentActivities = studentActivities.reduce((acc, cur) => {
        return {
            ...acc,
            [cur.id]: [...(acc[cur.id] || []), cur],
        };
    }, {});
    const teamChanges = Object.entries(reducedTeamActivities)
        .map(([id, activities]) => collapseTeamActivities(id, activities))
        .filter((item) => item);
    const studentChanges = Object.entries(reducedStudentActivities)
        .map(([id, activities]) => collapseStudentActivities(id, activities))
        .filter((item) => item)
        .filter(({ payload }) => payload.from !== payload.to);
    return { teamChanges, studentChanges };
};

const getLowestUniqueGroupNumber = (groups) => {
    for (let i = 1; i <= groups.length; i++) {
        if (!groups.find(({ number }) => number === i.toString())) {
            return i.toString();
        }
    }
    return (groups.length + 1).toString();
};

const EditTeams = () => {
    const { isLoading, course } = useApp();
    const { device } = useViewport();
    const { teamWS } = useWebsocket();

    const { isWSLoading, setDialog, editTeams, setEditTeams } = useContext(PageContext);

    const { open, type, groupSize, numGroups, groups, studentAccountUsers, onSubmit } = editTeams;

    const [teams, setTeams] = useState(groups); // working copy of groups
    const [students, setStudents] = useState(studentAccountUsers); // working copy of student accountUsers
    const [selectedTeamId, setSelectedTeamId] = useState(''); // currently selected group
    const [searchInput, setSearchInput] = useState(''); // filter input value for unassigned students
    const [activities, setActivities] = useState([]); // history of user activities
    const [finalizedActivities, setFinalizedActivities] = useState(null); // list of finalized activities ready for backend

    const addTeam = () => {
        const id = uuid();
        const groupNumber = getLowestUniqueGroupNumber(teams);
        const newTeam = {
            id,
            name: `Team ${groupNumber}`,
            number: groupNumber,
        };
        setTeams((current) => [...current, newTeam]);
        setSelectedTeamId(id);
        setActivities((current) => [
            ...current,
            {
                id,
                action: 'create',
                payload: newTeam,
                created_on: new Date().getTime(),
            },
        ]);
    };

    const removeTeam = (id) => {
        setTeams((current) => current.filter((group) => group.id !== id));
        setSelectedTeamId('');
        students.filter((student) => student.group_id === id).forEach((student) => removeStudentFromGroup(student, id));
        setActivities((current) => [
            ...current,
            {
                id,
                action: 'delete',
                created_on: new Date().getTime(),
            },
        ]);
    };

    const addStudentToGroup = ({ owner_account_id, user_id }, group) => {
        setStudents((current) =>
            current.map((student) => {
                return student.owner_account_id === owner_account_id
                    ? {
                          ...student,
                          group_id: group.id,
                      }
                    : student;
            })
        );
        setActivities((current) => [
            ...current,
            {
                id: user_id,
                action: 'move',
                payload: { from: null, to: group.id },
                created_on: new Date().getTime(),
            },
        ]);
    };

    const removeStudentFromGroup = ({ owner_account_id, user_id }, id) => {
        setStudents((current) =>
            current.map((student) => {
                return student.owner_account_id === owner_account_id
                    ? {
                          ...student,
                          group_id: '',
                      }
                    : student;
            })
        );
        setActivities((current) => [
            ...current,
            {
                id: user_id,
                action: 'move',
                payload: { from: id || selectedTeam.id, to: null },
                created_on: new Date().getTime(),
            },
        ]);
    };

    const handleSelect = ({ key }) => {
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete Team?',
                description: 'All students assigned will be removed from the team.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    removeTeam(selectedTeam.id);
                    setDialog((current) => ({ ...current, open: false }));
                },
            });
        }
    };

    const handleChangeTeamName = (e) => {
        setTeams((current) =>
            current.map((team) => {
                return team.id === selectedTeam.id
                    ? {
                          ...team,
                          name: e.target.value,
                      }
                    : team;
            })
        );
        setActivities((current) => [
            ...current,
            {
                id: selectedTeam.id,
                action: 'update',
                payload: { name: e.target.value },
                created_on: new Date().getTime(),
            },
        ]);
    };

    const cleanUpEmptyTeams = () => {
        const emptyTeams = teams.filter((group) => {
            const numStudents = students.filter((student) => student.group_id === group.id).length;
            return !Boolean(numStudents);
        });
        setTeams((current) => {
            return current.filter((group) => {
                const isEmpty = emptyTeams.some((emptyTeam) => emptyTeam.id === group.id);
                return !isEmpty;
            });
        });
        setActivities((current) => {
            const newActivities = emptyTeams.map((group) => {
                return {
                    id: group.id,
                    action: 'delete',
                    created_on: new Date().getTime(),
                };
            });
            const finalizedActivities = [...current, ...newActivities];
            setFinalizedActivities(finalizedActivities);
            return finalizedActivities;
        });
    };

    const saveChanges = () => {
        cleanUpEmptyTeams();
    };

    const resetChanges = () => {
        setDialog({
            open: true,
            message: 'Discard Changes?',
            description: 'Are you sure you want to discard all changes you made?',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Reset',
            cancelButtonText: 'Cancel',
            onAction: () => {
                setTeams(groups);
                setStudents(studentAccountUsers);
                setActivities([]);
                setDialog((current) => ({ ...current, open: false }));
            },
        });
    };

    const handleClose = () => {
        if (isWSLoading) return;
        if (!activities.length) {
            setEditTeams({
                open: false,
                type: '',
                groupSize: '',
                numGroups: '',
                groups: [],
                studentAccountUsers: [],
                onSubmit: () => {},
            });
        } else {
            setDialog({
                open: true,
                message: 'Unsaved Changes',
                description: 'Changes you made so far will not be saved.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Leave',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setEditTeams({
                        open: false,
                        type: '',
                        groupSize: '',
                        numGroups: '',
                        groups: [],
                        studentAccountUsers: [],
                        onSubmit: () => {},
                    });
                    setTeams(groups);
                    setStudents(studentAccountUsers);
                    setSelectedTeamId('');
                    setSearchInput('');
                    setActivities([]);
                    setFinalizedActivities(null);
                    setDialog((current) => ({ ...current, open: false }));
                },
            });
        }
    };

    useEffect(() => {
        if (open && ['group-size', 'group-num', 'individual-size'].includes(type)) {
            const timestamp = new Date().getTime();
            let threshold;
            if (type === 'group-size' || type === 'individual-size') {
                threshold = Number(groupSize);
            }
            if (type === 'group-num') {
                threshold = Math.ceil(studentAccountUsers.length / Number(numGroups));
            }
            const shuffledStudents = _.cloneDeep(studentAccountUsers).sort(() => Math.random() - 0.5);
            chunk(shuffledStudents, threshold).forEach((students, index) => {
                const id = uuid();
                const newTeam = {
                    id,
                    name: `Team ${index + 1}`,
                    number: (index + 1).toString(),
                };
                setTeams((current) => [...(current || groups), newTeam]);
                setStudents((current) => {
                    return [
                        ...(current || studentAccountUsers).filter(
                            ({ user_id }) => !students.some((s) => s.user_id === user_id)
                        ),
                        ...students.map((student) => ({ ...student, group_id: id })),
                    ];
                });
                setActivities((current) => [
                    ...current,
                    {
                        id,
                        action: 'create',
                        payload: newTeam,
                        created_on: timestamp,
                    },
                    ...students.map((student) => ({
                        id: student.user_id,
                        action: 'move',
                        payload: { from: null, to: id },
                        created_on: timestamp,
                    })),
                ]);
            });
        }
    }, [open, type, groupSize, numGroups, groups, studentAccountUsers]);

    useEffect(() => {
        if (finalizedActivities) {
            const changes = getChanges(finalizedActivities);
            onSubmit(changes);
        }
    }, [finalizedActivities, course.id]);

    useEffect(() => {
        if (!teamWS) return;
        const { payload_type } = teamWS;
        if (payload_type === 'updated_teams') {
            setActivities([]);
        }
    }, [teamWS]);

    useEffect(() => {
        if (!open) {
            setTeams([]);
            setStudents([]);
            setActivities([]);
        }
    }, [open]);

    useEffect(() => {
        if (open && !['group-size', 'group-num', 'individual-size'].includes(type)) setTeams(groups);
    }, [open, type, groups]);

    useEffect(() => {
        if (open && !['group-size', 'group-num', 'individual-size'].includes(type)) setStudents(studentAccountUsers);
    }, [open, type, studentAccountUsers]);

    const selectedTeam = teams?.find(({ id }) => id === selectedTeamId);
    const selectedTeamStudents = students?.filter(({ group_id }) => group_id === selectedTeam?.id);
    const unassignedStudents = students?.filter(({ group_id }) => !group_id);

    if (!open || isLoading || !teams || !students) return <></>;

    const NoTeams = (
        <NoTeamsText align={'center'} color={'light'} variant={'sh2'}>
            <CustomIcon kind={'teams'} />
            {teams.length && !selectedTeam ? 'Select a team to make changes.' : 'Add a team to get started.'}
        </NoTeamsText>
    );

    const TeamSelect = (
        <Stack sx={{ height: '100%' }} spacing={3}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    {device === 'mobile' && (
                        <IconButton
                            aria-label={'back'}
                            disabled={isWSLoading}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    )}
                    <Text variant={'h6'}>Teams</Text>
                </Stack>
                <IconButton
                    aria-label={'create new team'}
                    disabled={isWSLoading}
                    kind={'add'}
                    size={'small'}
                    variant={'circle'}
                    onClick={addTeam}
                />
            </Stack>
            {device === 'mobile' && !teams.length ? (
                NoTeams
            ) : (
                <TeamButtonStack>
                    {teams.map((team) => {
                        const members = students.filter(({ group_id }) => group_id === team.id);
                        return (
                            <TeamButton
                                key={team.id}
                                selected={selectedTeamId === team.id}
                                onClick={() => setSelectedTeamId(team.id)}
                            >
                                <Stack sx={{ width: '100%' }} alignItems={'flex-start'} spacing={1}>
                                    <Text noWrap variant={'sh2'}>
                                        {members.length === 1 ? getUserPreferredFullName(members[0]) : team.name}
                                    </Text>
                                    <Text color={'light'} variant={'p2'}>
                                        Members ({members.length})
                                    </Text>
                                </Stack>
                            </TeamButton>
                        );
                    })}
                </TeamButtonStack>
            )}
        </Stack>
    );

    const TeamEdit = (
        <Stack sx={{ height: '100%' }} spacing={4}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack sx={{ minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
                    {device === 'mobile' && (
                        <IconButton
                            aria-label={'back'}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => setSelectedTeamId('')}
                        />
                    )}
                    <Text noWrap variant={'h6'}>
                        {selectedTeam
                            ? `Edit Teams - ${
                                  selectedTeamStudents.length === 1
                                      ? getUserPreferredFullName(selectedTeamStudents[0])
                                      : selectedTeam.name
                              }`
                            : `Edit Teams`}
                    </Text>
                </Stack>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    {selectedTeam && (
                        <Dropdown
                            id={'edit-team-dropdown'}
                            options={[{ key: 'delete', value: 'Delete', disabled: isWSLoading }]}
                            rootComponent={
                                <IconButton
                                    aria-label={'more options'}
                                    kind={'moreHorizontal'}
                                    size={'medium'}
                                    variant={'square'}
                                />
                            }
                            onSelect={handleSelect}
                        />
                    )}
                    {device !== 'mobile' && (
                        <IconButton
                            aria-label={'close'}
                            disabled={isWSLoading}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    )}
                </Stack>
            </Stack>
            {selectedTeam ? (
                <EditTeamStack spacing={4}>
                    <TextInput
                        disabled={isWSLoading || selectedTeamStudents.length === 1}
                        label={'Team Name'}
                        value={
                            selectedTeamStudents.length === 1
                                ? getUserPreferredFullName(selectedTeamStudents[0])
                                : selectedTeam.name
                        }
                        onChange={handleChangeTeamName}
                    />
                    <Stack spacing={2}>
                        <Text variant={'p2'}>Team Members ({selectedTeamStudents.length})</Text>
                        <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={2}>
                            {selectedTeamStudents.map((student) => (
                                <Chip
                                    key={student.owner_account_id}
                                    content={getUserPreferredFullName(student)}
                                    disabled={isWSLoading}
                                    onClick={() => removeStudentFromGroup(student)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    <Stack spacing={2}>
                        <TextInput
                            endIcon={<Icon kind={'search'} />}
                            label={`Unassigned Students (${unassignedStudents.length})`}
                            placeholder={'Search'}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                        <Stack divider={<Divider />}>
                            {unassignedStudents
                                .filter((student) => {
                                    return getUserPreferredFullName(student)
                                        .toLowerCase()
                                        .includes(searchInput.toLowerCase());
                                })
                                .map((student, index) => (
                                    <CustomStack key={student.owner_account_id} index={index}>
                                        <CustomCheckbox
                                            checked={false}
                                            disabled={isWSLoading}
                                            label={getUserPreferredFullName(student)}
                                            labelPlacement={'start'}
                                            onChange={() => addStudentToGroup(student, selectedTeam)}
                                        />
                                    </CustomStack>
                                ))}
                        </Stack>
                    </Stack>
                </EditTeamStack>
            ) : (
                NoTeams
            )}
        </Stack>
    );

    return (
        <>
            <CustomResponsiveDialog open={open} onClose={handleClose}>
                <DialogInnerBox device={device}>
                    {device === 'mobile' ? (
                        <>
                            {TeamSelect}
                            <ResponsiveDialog open={Boolean(selectedTeamId)} onClose={() => setSelectedTeamId('')}>
                                {TeamEdit}
                            </ResponsiveDialog>
                        </>
                    ) : (
                        <DialogInnerStack
                            direction={'row'}
                            divider={<Divider orientation={'vertical'} />}
                            hasChanges={Boolean(activities.length)}
                        >
                            <LeftBox>{TeamSelect}</LeftBox>
                            <RightBox>{TeamEdit}</RightBox>
                        </DialogInnerStack>
                    )}
                    {activities.length ? (
                        <StickyBottomBox device={device}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text color={isWSLoading ? 'light' : 'error'}>
                                    {isWSLoading ? 'Saving...' : 'Caution - You have unsaved changes!'}
                                </Text>
                                <Stack direction={'row'} spacing={4}>
                                    <Button
                                        color={'neutral'}
                                        disabled={isWSLoading}
                                        variant={'tertiary'}
                                        onClick={resetChanges}
                                    >
                                        Discard Changes
                                    </Button>
                                    <Button loading={isWSLoading} onClick={saveChanges}>
                                        Save Changes
                                    </Button>
                                </Stack>
                            </Stack>
                        </StickyBottomBox>
                    ) : null}
                </DialogInnerBox>
            </CustomResponsiveDialog>
        </>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ theme }) => {
    return {
        '& .MuiDialog-paper > .MuiBox-root': {
            padding: theme.spacing(0, 7, 0),
        },
        '& .MuiDrawer-paper > .MuiBox-root': {
            padding: theme.spacing(5, 5, 0),
        },
    };
});

const DialogInnerBox = styled(Box)(({ device }) => {
    return {
        position: 'relative',
        width: '40rem',
        height: '75vh',
        ...(device === 'mobile' && {
            width: '100%',
            height: 'calc(100vh - 84px)',
        }),
    };
});

const DialogInnerStack = styled(Stack)(({ hasChanges }) => {
    return {
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...(hasChanges && {
            height: 'calc(100% - 63px)',
        }),
    };
});

const LeftBox = styled(Box)(({ theme }) => {
    return {
        flex: 1,
        marginRight: theme.spacing(6),
        minWidth: 0,
        paddingTop: theme.spacing(7),
        paddingBottom: theme.spacing(6),
    };
});

const TeamButtonStack = styled(Stack)(() => {
    return {
        overflowY: 'auto',
    };
});

const TeamButton = styled(ButtonBase)(({ selected, theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(3, 2),
        '&:hover': {
            background: theme.palette.ensightful.primary[100],
        },
        '&:active': {
            background: theme.palette.ensightful.primary[200],
        },
        ...(selected && {
            background: theme.palette.ensightful.primary[50],
        }),
    };
});

const RightBox = styled(Box)(({ theme }) => {
    return {
        flex: 2,
        marginLeft: theme.spacing(6),
        minWidth: 0,
        paddingTop: theme.spacing(7),
    };
});

const EditTeamStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(0, -1),
        height: 'calc(100% - 48px)',
        padding: theme.spacing(0, 1),
        overflowY: 'auto',
    };
});

const CustomStack = styled(Stack)(({ index, theme }) => {
    return {
        padding: theme.spacing(1, 3),
        background: !(index % 2) ? theme.palette.ensightful.neutral[100] : theme.palette.ensightful.shades[0],
    };
});

const CustomCheckbox = styled(Checkbox)(() => {
    return {
        justifyContent: 'space-between',
    };
});

const NoTeamsText = styled(Text)(() => {
    return {
        marginTop: 'calc((100vh - 32px) / 5)',
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        display: 'block',
        margin: theme.spacing(0, 'auto', 2),
        fontSize: '3rem',
    };
});

const StickyBottomBox = styled(Box)(({ device, theme }) => {
    return {
        position: 'absolute',
        bottom: 0,
        left: 0,
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        margin: theme.spacing(0, -7),
        width: `calc(100% + ${theme.spacing(14)})`,
        padding: theme.spacing(3, 7),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            margin: theme.spacing(0, -5),
            width: `calc(100% + ${theme.spacing(10)})`,
            padding: theme.spacing(3, 4),
        }),
    };
});

export default EditTeams;
